import React, { useState } from "react";
import { Loader2, Pencil } from "lucide-react";
import { api, getApiErrorMessage } from "../../api/client";
import FleetUnitPicker, { fleetUnitLabel, useFleetUnit } from "./FleetUnitPicker";

/**
 * The truck and trailer on one load, on its bill of lading.
 *
 * Editable until the BOL is signed (operator, 2026-10-14). After the customer
 * signs, the truck and trailer are part of what was signed for, so the panel is
 * read-only, the same point at which the rates lock. That lock is the
 * frontend's: PATCH /trucking/loads/:id does not refuse a truck or trailer
 * change on a signed load, only a rate or quantity change.
 *
 * PATCH sends only what changed, camelCase: `truckId` / `trailerId`, the
 * unit's id or `null` to clear (api#1045). The API's refusals are shown as
 * sent: 404 `trailerId not found`, 422 `TRAILER_NOT_A_TRAILER`, 409 for a
 * retired or out-of-service unit.
 *
 * The BOL read is `l.*` (snake_case) plus the truck's unit number; it joins no
 * trailer, so the trailer's label comes from the trailer list.
 */
interface LoadLike {
  id: string | number;
  status?: string | null;
  signed_at?: string | null;
  truck_id?: string | null;
  trailer_id?: string | null;
  truck_unit_number?: string | null;
}

const str = (v: unknown) => (typeof v === "string" && v.trim() ? v : "");

export default function LoadEquipmentPanel({ load, onSaved }: { load: LoadLike; onSaved: () => void }) {
  const signed = !!load.signed_at;
  const cancelled = load.status === "cancelled";
  const truckId = str(load.truck_id);
  const trailerId = str(load.trailer_id);
  const [editing, setEditing] = useState(false);
  const [truck, setTruck] = useState(truckId);
  const [trailer, setTrailer] = useState(trailerId);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const trailerUnit = useFleetUnit(trailerId, "trailer");

  const truckText = truckId ? str(load.truck_unit_number) || "—" : "No truck";
  // A trailer the list cannot name (retired since, or the list failed) is a dash, never "No trailer".
  const trailerText = trailerId ? (trailerUnit ? fleetUnitLabel(trailerUnit) : "—") : "No trailer";

  const start = () => {
    setTruck(truckId);
    setTrailer(trailerId);
    setRefusal(null);
    setEditing(true);
  };

  const save = async () => {
    const body: Record<string, unknown> = {};
    if (truck !== truckId) body.truckId = truck || null;
    if (trailer !== trailerId) body.trailerId = trailer || null;
    if (Object.keys(body).length === 0) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setRefusal(null);
    try {
      await api.trucking.loads.update(String(load.id), body);
      setEditing(false);
      onSaved();
    } catch (err) {
      setRefusal(getApiErrorMessage(err, "The truck and trailer were not saved."));
    } finally {
      setSaving(false);
    }
  };

  const select =
    "h-10 w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none";

  return (
    <section
      aria-labelledby="load-equipment-title"
      data-testid="load-equipment-panel"
      className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
    >
      <header className="flex flex-wrap items-start justify-between gap-3 border-b border-fieldiq-border px-4 py-4">
        <div>
          <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Equipment</p>
          <h2 id="load-equipment-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Truck and trailer</h2>
        </div>
        {!signed && !cancelled && !editing && (
          <button
            type="button"
            onClick={start}
            className="inline-flex items-center gap-1.5 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm font-medium text-fieldiq-text hover:bg-fieldiq-dark/50"
          >
            <Pencil className="size-4" aria-hidden="true" />
            Edit truck and trailer
          </button>
        )}
      </header>

      {editing ? (
        <div className="space-y-4 px-4 py-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="bol-truck" className="mb-1 block text-xs font-medium text-fieldiq-muted">Truck</label>
              <FleetUnitPicker id="bol-truck" purpose="load" kind="truck" value={truck} onChange={setTruck} className={select} />
            </div>
            <div>
              <label htmlFor="bol-trailer" className="mb-1 block text-xs font-medium text-fieldiq-muted">Trailer</label>
              <FleetUnitPicker id="bol-trailer" purpose="load" kind="trailer" value={trailer} onChange={setTrailer} className={select} />
            </div>
          </div>
          {refusal && <p role="alert" data-testid="load-equipment-refusal" className="text-sm font-medium text-fieldiq-text">{refusal}</p>}
          <div className="flex gap-2">
            <button type="button" onClick={save} disabled={saving} className="fi-action fi-action--standard fi-action--primary disabled:opacity-50">
              {saving && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
              Save truck and trailer
            </button>
            <button type="button" onClick={() => setEditing(false)} className="rounded-fi-control border border-fieldiq-border px-4 py-2.5 text-sm font-medium text-fieldiq-text hover:bg-fieldiq-dark/50">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <dl className="grid gap-x-8 gap-y-3 px-4 py-4 sm:grid-cols-2">
          <div data-testid="load-equipment-truck">
            <dt className="text-[10px] font-bold uppercase tracking-wider text-fieldiq-muted">Truck</dt>
            <dd className="mt-0.5 font-mono text-sm text-fieldiq-text">{truckText}</dd>
          </div>
          <div data-testid="load-equipment-trailer">
            <dt className="text-[10px] font-bold uppercase tracking-wider text-fieldiq-muted">Trailer</dt>
            <dd className="mt-0.5 font-mono text-sm text-fieldiq-text">{trailerText}</dd>
          </div>
        </dl>
      )}

      {signed && (
        <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">Signed: the truck and trailer are final.</p>
      )}
    </section>
  );
}
